import { useTranslation, Trans } from 'react-i18next';

import { Flex } from '@spotnana/pixel-react/dist/Box';
import * as Icons from '@spotnana/pixel-react/dist/Icons';
import spotnanaTheme from '@spotnana/pixel-react/dist/utils/themes/theme';
import Divider from '@spotnana/pixel-react/dist/Divider';
import Grid from '@spotnana/pixel-react/dist/Grid';
import { Typography } from '@spotnana/blocks/src/Typography';

import { Icon } from '@spotnana/blocks/src/Icon/Icon';
import { AirSearchResponseManager } from 'obt-common';
import { TravelType } from '@spotnana/types/openapi/models';
import { Tooltip } from '@spotnana/blocks/src/Tooltip';
import { CheckoutInfoContainer } from '../../app/flights/shared/CheckoutInfoContainer';
import { useGetCo2EmissionsTooltipText } from './CO2Emissions/useGetCo2EmissionsTooltipText';

interface ISegmentInfo<T> {
  origin: T;
  destination: T;
}

type LegWiseEmissionsInfoProps<T> = {
  emissions: (number | null)[];
  segments: ISegmentInfo<T>[];
  paxNumber: number;
  getShortNameForLocation: (segment: T) => string;
  travelType?: TravelType;
};

function LegWiseEmissionsInfo<T>({
  segments,
  emissions,
  paxNumber,
  getShortNameForLocation,
  travelType = TravelType.Air,
}: LegWiseEmissionsInfoProps<T>): JSX.Element {
  const { t: tt } = useTranslation('WEB');
  // Round the emissions before calculating the total to fix edge case that can cause the total to be off by 0.1
  const roundedEmissions = emissions.map((emission) => (emission ? Math.round(emission * 10) / 10 : null));
  const totalEmissionsValue = AirSearchResponseManager.GetTotalEmissionsForEmissionValues(roundedEmissions, 'kg');
  const totalEmissions = totalEmissionsValue ? Math.round(totalEmissionsValue * 10) / 10 : null;
  const paxTotalEmissions = paxNumber * (totalEmissions ?? 0);

  const isSomeDataAvailable = emissions.some((emissionValue) => emissionValue !== null);

  const tooltipText = useGetCo2EmissionsTooltipText(travelType);

  return (
    <CheckoutInfoContainer data-testid="co2-emissions-container">
      <Flex justifyContent="space-between" alignItems="center">
        <Flex justifyContent="flex-start" alignItems="center">
          <Icons.Eco width="24px" height="24px" primaryColor={spotnanaTheme.colors.text.darkSeaGreen} />

          <Typography color="success" variant="header5" as="h2" kind="medium" style={{ marginLeft: '2px' }}>
            <Trans t={tt}>
              CO<sub>2</sub> emissions
            </Trans>
          </Typography>

          <Tooltip
            variant="info"
            placement="top"
            title={
              <Typography variant="body1" kind="medium" color="buttonLabel">
                {tooltipText || (
                  <Trans t={tt}>
                    Estimated CO<sub>2</sub>
                  </Trans>
                )}
              </Typography>
            }
          >
            <Flex justifyContent="center" alignItems="center" marginLeft="8px">
              <Icons.Info
                width="14px"
                height="14px"
                primaryColor={spotnanaTheme.colors.text.darkSeaGreen}
                style={{ cursor: 'pointer' }}
              />
            </Flex>
          </Tooltip>
        </Flex>

        {!isSomeDataAvailable && (
          <Typography variant="body2" kind="semibold" data-testid="co2-emissions-no-data">
            {tt('Not available')}
          </Typography>
        )}
      </Flex>

      {isSomeDataAvailable && (
        <>
          <Divider my="12px" />

          {emissions
            .filter((_, index) => segments[index] !== undefined)
            .map((emission: number | null, index: number) => (
              <Grid.Row justifyContent="space-between" key={index} style={{ marginBottom: 12 }}>
                <Grid.Col xs>
                  <Typography variant="body1" id={`segment-${index}-emissions`}>
                    {getShortNameForLocation(segments[index].origin)}{' '}
                    <span style={{ verticalAlign: 'middle' }}>
                      <Icon name="ArrowRight" title={tt('to')} variant="standalone" />
                    </span>{' '}
                    {getShortNameForLocation(segments[index].destination)}
                  </Typography>
                </Grid.Col>
                <Grid.Col xs={4}>
                  <Typography variant="body1" style={{ textAlign: 'end' }}>
                    {emission === null ? (
                      tt('Not available')
                    ) : (
                      <Trans t={tt} values={{ emission: emission.toFixed(1) }}>
                        {'{{emission}}'} kg CO<sub>2</sub>
                      </Trans>
                    )}
                  </Typography>
                </Grid.Col>
              </Grid.Row>
            ))}
        </>
      )}

      {totalEmissions !== null && (
        <>
          <Divider mt="12px" mb="18px" />

          <Grid.Row justifyContent="space-between" alignItems="center">
            <Grid.Col xs>
              <Typography id="total-emissions" variant="body1" kind="semibold">
                {paxNumber > 1 ? tt('Total (x{{count}} traveler)', { count: paxNumber }) : tt('Total')}
              </Typography>
            </Grid.Col>
            <Grid.Col xs={4}>
              <Typography variant="body1" kind="semibold" style={{ textAlign: 'end' }}>
                <Trans t={tt} values={{ emissionValue: paxTotalEmissions.toFixed(1) }}>
                  {'{{emissionValue}}'} kg CO<sub>2</sub>
                </Trans>
              </Typography>
            </Grid.Col>
          </Grid.Row>
        </>
      )}
    </CheckoutInfoContainer>
  );
}

export default LegWiseEmissionsInfo;
